// BM-H398 - s8_digital. `include_digital_assets_clause` plus holdings drive
// C-17; the same toggle plus an appointed digital executor drives C-18.

import { QuestionRow, RepeatList, SectionIntro, Select, TextInput, YesNo, sectionProps, replaceAt, removeAt } from "./fields";
import PersonPicker from "./PersonPicker";
import { newId } from "./willsPayload";
import { CREDENTIAL_FIELD_WARNING, looksLikeCredential } from "./credentialCheck";

const HOLDING_TYPES = [
  { value: "email", label: "Email" },
  { value: "social_media", label: "Social media" },
  { value: "cloud_photos", label: "Photos or files in the cloud" },
  { value: "crypto", label: "Cryptocurrency" },
  { value: "online_banking", label: "Online banking or payment accounts" },
  { value: "domain", label: "Domain names or websites" },
  { value: "other", label: "Other" },
];

const DISPOSITION = [
  { value: "to_executors", label: "My executors deal with them" },
  { value: "specific_gift", label: "A specific person receives them" },
];

// BM-H3960 item 37: shown above the list. The backend refuses a holding that looks like a credential and never repeats it.
export const NO_CREDENTIALS_NOTICE = "Never type a password, key or recovery phrase here. Describe the account and where it is held, for example \"Family photographs in an iCloud account held with Apple\".";

// BM-H4006: shown the moment a field looks like a password, key or recovery phrase. It never repeats what was typed. The text is not
// saved: the questionnaire does not send it (ClientFormWills.save) and the server would refuse it.
function CredentialWarning({ testid }) {
  return (
    <p data-testid={testid} role="alert" style={{ fontSize: 13, margin: "6px 0 0", padding: "6px 10px", border: "1px solid currentColor", borderRadius: 8, fontWeight: 600 }}>
      {CREDENTIAL_FIELD_WARNING}
    </p>
  );
}

export default function S8Digital({ section, setSection, payload, update, person }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const holdings = s.holdings || [];
  const de = s.digital_executor || {};

  return (
    <div data-testid="section-s8_digital">
      <SectionIntro title="Digital assets">
        Online accounts, photos, cryptocurrency and domain names. Names of providers only, never passwords.
      </SectionIntro>

      <QuestionRow qid="s8_digital.include_digital_assets_clause" label="Would you like your will to include a digital assets clause?" notKnown={nk("include_digital_assets_clause")} onNotKnown={(on) => setNk("include_digital_assets_clause", on)}>
        <YesNo testid="s8-include" value={s.include_digital_assets_clause} disabled={nk("include_digital_assets_clause")} onChange={(v) => set({ include_digital_assets_clause: v })} />
      </QuestionRow>

      {s.include_digital_assets_clause === true && (
        <>
          <p data-testid="s8-no-credentials-notice" role="note" style={{ fontSize: 13, margin: "0 0 12px", padding: "8px 12px", border: "1px solid currentColor", borderRadius: 8 }}>
            {NO_CREDENTIALS_NOTICE}
          </p>
          <QuestionRow qid="s8_digital.holdings" label="What digital assets do you hold?">
            <RepeatList
              testid="s8-holdings"
              items={holdings}
              onAdd={() => set({ holdings: [...holdings, { id: newId("dig"), type: null, provider: "", description: "" }] })}
              onRemove={(i) => set({ holdings: removeAt(holdings, i) })}
              addLabel="Add a digital asset"
              render={(h, i) => (
                <>
                  <QuestionRow qid="s8_digital.holdings.type" label="Kind" testid={`s8-holding-${i}-q-type`}>
                    <Select testid={`s8-holding-${i}-type`} value={h.type} options={HOLDING_TYPES} onChange={(v) => set({ holdings: replaceAt(holdings, i, { ...h, type: v }) })} />
                  </QuestionRow>
                  {/* BM-H4006: a description that looks like a credential stays visible whatever the kind, so it can always be deleted. */}
                  {(h.type === "other" || looksLikeCredential(h.description)) && (
                    <QuestionRow qid="s8_digital.holdings.description" label="Describe it: what it is, so your executors can recognise it" testid={`s8-holding-${i}-q-description`}>
                      <TextInput testid={`s8-holding-${i}-description`} value={h.description} onChange={(v) => set({ holdings: replaceAt(holdings, i, { ...h, description: v }) })} />
                      {looksLikeCredential(h.description) && <CredentialWarning testid={`s8-holding-${i}-description-credential-warning`} />}
                    </QuestionRow>
                  )}
                  <QuestionRow qid="s8_digital.holdings.provider" label={h.type === "other" ? "Where it is held, or with whom" : "Provider"} testid={`s8-holding-${i}-q-provider`}>
                    <TextInput testid={`s8-holding-${i}-provider`} value={h.provider} onChange={(v) => set({ holdings: replaceAt(holdings, i, { ...h, provider: v }) })} />
                    {looksLikeCredential(h.provider) && <CredentialWarning testid={`s8-holding-${i}-provider-credential-warning`} />}
                  </QuestionRow>
                </>
              )}
            />
          </QuestionRow>
          <QuestionRow qid="s8_digital.disposition" label="Who should deal with them?">
            <Select testid="s8-disposition" value={s.disposition} options={DISPOSITION} onChange={(v) => set({ disposition: v, specific_gift_beneficiary_id: v === "specific_gift" ? s.specific_gift_beneficiary_id : null })} />
          </QuestionRow>
          {s.disposition === "specific_gift" && (
            <PersonPicker payload={payload} update={update} qid="s8_digital.specific_gift_beneficiary_id" label="Who receives them?" value={s.specific_gift_beneficiary_id} exclude={[person?.id]} onChange={(pid) => set({ specific_gift_beneficiary_id: pid })} testid="s8-beneficiary" />
          )}
          <QuestionRow qid="s8_digital.digital_executor.appointed" label="Would you like to appoint a digital executor?">
            <YesNo testid="s8-digital-executor" value={de.appointed ?? null} onChange={(v) => set({ digital_executor: { appointed: v, person_id: v ? de.person_id : null } })} />
          </QuestionRow>
          {de.appointed === true && (
            <PersonPicker payload={payload} update={update} qid="s8_digital.digital_executor.person_id" label="Who?" value={de.person_id} exclude={[person?.id]} onChange={(pid) => set({ digital_executor: { ...de, person_id: pid } })} testid="s8-digital-executor-person" />
          )}
          <QuestionRow qid="s8_digital.access_memorandum_acknowledged" label="Will you keep a separate note of access instructions with your will?">
            <YesNo testid="s8-access-memo" value={s.access_memorandum_acknowledged} onChange={(v) => set({ access_memorandum_acknowledged: v })} />
          </QuestionRow>
        </>
      )}
    </div>
  );
}
